import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Trash2, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "open", "in_progress", "waiting", "resolved", "closed"];
const PRIORITIES = ["all", "low", "medium", "high", "critical"];
const STATUS_COLORS = { open: "bg-yellow-500/10 text-yellow-400", in_progress: "bg-[#F59073]/10 text-[#F59073]", waiting: "bg-orange-500/10 text-orange-400", resolved: "bg-green-500/10 text-green-400", closed: "bg-neutral-500/10 text-[#857973]" };
const PRIO_COLORS = { low: "text-[#857973]", medium: "text-yellow-400", high: "text-orange-400", critical: "text-red-400" };

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [form, setForm] = useState({ customer_id: "", title: "", description: "", priority: "medium", assigned_to: "" });

  const fetch = useCallback(async () => {
    const [tickRes, custRes] = await Promise.all([
      axios.get(`${API}/admin/tickets`, { params: { status: filterStatus, priority: filterPriority }, withCredentials: true }),
      axios.get(`${API}/admin/customers-list`, { withCredentials: true }),
    ]);
    setTickets(tickRes.data);
    setCustomers(custRes.data);
  }, [filterStatus, filterPriority]);

  useEffect(() => { fetch(); }, [fetch]);

  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";

  const createTicket = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/tickets`, form, { withCredentials: true });
    setShowCreate(false);
    setForm({ customer_id: "", title: "", description: "", priority: "medium", assigned_to: "" });
    fetch();
  };

  const updateTicket = async (id, updates) => {
    await axios.put(`${API}/admin/tickets/${id}`, updates, { withCredentials: true });
    fetch();
    if (editTicket?.id === id) {
      const { data } = await axios.get(`${API}/admin/tickets`, { params: { status: filterStatus, priority: filterPriority }, withCredentials: true });
      const updated = data.find(t => t.id === id);
      if (updated) setEditTicket(updated);
    }
  };

  const addComment = async () => {
    if (!commentText.trim() || !editTicket) return;
    await axios.post(`${API}/admin/tickets/${editTicket.id}/comments`, { text: commentText }, { withCredentials: true });
    setCommentText("");
    const { data } = await axios.get(`${API}/admin/tickets`, { params: { status: filterStatus, priority: filterPriority }, withCredentials: true });
    const updated = data.find(t => t.id === editTicket.id);
    if (updated) setEditTicket(updated);
    fetch();
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Delete this ticket?")) return;
    await axios.delete(`${API}/admin/tickets/${id}`, { withCredentials: true });
    fetch();
    setEditTicket(null);
  };

  return (
    <div data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-white">Support Tickets</h1><p className="text-sm text-[#857973]">{tickets.length} tickets</p></div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button data-testid="create-ticket-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all"><Plus className="w-4 h-4" /> New Ticket</button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F1F3A] border-[#1E3050] text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-outfit">Create Ticket</DialogTitle></DialogHeader>
            <form onSubmit={createTicket} className="space-y-4 mt-4">
              <select required value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-ticket-customer">
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <input required placeholder="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-ticket-title" />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" rows={3} />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                </select>
                <input placeholder="Assigned to" value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <button type="submit" data-testid="create-ticket-submit" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">Create Ticket</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterStatus === s ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>{s.replace("_", " ")}</button>
          ))}
        </div>
        <div className="flex gap-1">
          {PRIORITIES.map(p => (
            <button key={p} onClick={() => setFilterPriority(p)} className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterPriority === p ? "bg-orange-500/10 text-orange-400 border border-orange-500/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>{p}</button>
          ))}
        </div>
      </div>

      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1E3050]">
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">TICKET</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">CUSTOMER</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">PRIORITY</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">STATUS</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">ASSIGNED</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} className="border-b border-[#1E3050] hover:bg-[#1E3050]/20 cursor-pointer" onClick={() => setEditTicket(t)}>
                <td className="px-4 py-3"><span className="text-sm text-white">{t.title}</span><br/><span className="text-xs text-[#857973]">{(t.comments?.length || 0)} comments</span></td>
                <td className="px-4 py-3 text-sm text-[#857973]">{custName(t.customer_id)}</td>
                <td className="px-4 py-3"><span className={`text-xs font-mono font-medium ${PRIO_COLORS[t.priority] || "text-[#857973]"}`}>{t.priority}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${STATUS_COLORS[t.status] || "bg-[#1E3050]/40 text-[#857973]"}`}>{t.status?.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-sm text-[#857973]">{t.assigned_to || "—"}</td>
                <td className="px-4 py-3"><button onClick={(e) => { e.stopPropagation(); deleteTicket(t.id); }} className="text-[#857973] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
            {tickets.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#857973]">No tickets found</td></tr>}
          </tbody>
        </table>
      </div>

      {editTicket && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditTicket(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-[#0F1F3A] border-l border-[#1E3050] overflow-y-auto z-10 p-6">
            <div className="flex justify-between mb-6">
              <h2 className="font-outfit font-bold text-xl text-white">{editTicket.title}</h2>
              <div className="flex gap-2">
                <button onClick={() => deleteTicket(editTicket.id)} className="text-[#857973] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                <button onClick={() => setEditTicket(null)} className="text-[#857973] hover:text-white text-xl">&times;</button>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div><span className="font-mono text-[10px] text-[#857973]">CUSTOMER</span><p className="text-sm text-white">{custName(editTicket.customer_id)}</p></div>
              <div><span className="font-mono text-[10px] text-[#857973]">DESCRIPTION</span><p className="text-sm text-neutral-300">{editTicket.description || "—"}</p></div>
              <div>
                <span className="font-mono text-[10px] text-[#857973] block mb-2">STATUS</span>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.filter(s => s !== "all").map(s => (
                    <button key={s} onClick={() => updateTicket(editTicket.id, { status: s })}
                      className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${editTicket.status === s ? (STATUS_COLORS[s] + " border-current") : "bg-[#1E3050]/40 text-[#857973] border-[#1E3050] hover:text-white"}`}>{s.replace("_", " ")}</button>
                  ))}
                </div>
              </div>
              <div>
                <span className="font-mono text-[10px] text-[#857973] block mb-2">PRIORITY</span>
                <div className="flex gap-1.5">
                  {PRIORITIES.filter(p => p !== "all").map(p => (
                    <button key={p} onClick={() => updateTicket(editTicket.id, { priority: p })}
                      className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${editTicket.priority === p ? "bg-orange-500/10 text-orange-400 border-orange-500/30" : "bg-[#1E3050]/40 text-[#857973] border-[#1E3050] hover:text-white"}`}>{p}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-[#1E3050] pt-6">
              <h3 className="font-outfit font-medium text-white mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Comments</h3>
              <div className="space-y-3 mb-4">
                {(editTicket.comments || []).map((c, i) => (
                  <div key={i} className="bg-black/40 border border-[#1E3050] rounded-sm p-3">
                    <p className="text-sm text-neutral-300">{c.text}</p>
                    <p className="text-xs text-[#857973] mt-1">{c.author} &middot; {c.created_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add comment..." data-testid="ticket-comment-input"
                  className="flex-1 bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" onKeyDown={e => e.key === "Enter" && addComment()} />
                <button onClick={addComment} data-testid="ticket-comment-submit" className="bg-white/10 text-white px-4 py-2 rounded-sm text-sm hover:bg-white/20 transition-colors">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
